import type { DiscoveryCostQuote } from '@discovr/types';
import { NO_VALUE } from './constants/discoveryCopy';
import { COST_COPY, POSTING_LABEL } from './constants/findCopy';
import {
  METRIC_DECIMALS,
  POSTING_DAILY_MAX_DAYS,
  ROUNDED_THOUSANDS_FROM,
} from './constants/findLimits';
import { MILLION, PERCENT, SECONDS_PER_MINUTE, THOUSAND } from './constants/units';

export function fmtCount(n: number): string {
  const thousands = (n / THOUSAND).toFixed(n >= ROUNDED_THOUSANDS_FROM ? 0 : 1);
  if (n >= MILLION || Number(thousands) * THOUSAND >= MILLION)
    return `${(n / MILLION).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= THOUSAND) return `${thousands.replace(/\.0$/, '')}K`;
  return String(Math.round(n));
}

export const percentText = (value: number | null) =>
  value === null ? NO_VALUE : `${value.toFixed(METRIC_DECIMALS)}%`;
export const timesText = (value: number | null) =>
  value === null ? NO_VALUE : `${value.toFixed(METRIC_DECIMALS)}×`;
export const countText = (value: number | null) => (value === null ? NO_VALUE : fmtCount(value));
export const scoreText = (value: number | null, max: number) =>
  value === null ? NO_VALUE : `${value.toFixed(METRIC_DECIMALS)}/${max}`;
export const percentOf = (ratio: number | null) =>
  ratio === null ? null : Math.round(ratio * PERCENT);

export function duration(seconds: number | null): string | null {
  if (seconds === null) return null;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / SECONDS_PER_MINUTE)}:${String(whole % SECONDS_PER_MINUTE).padStart(2, '0')}`;
}

export function postingLabel(avgGapDays: number | null): string | null {
  if (avgGapDays === null) return null;
  if (avgGapDays <= POSTING_DAILY_MAX_DAYS) return POSTING_LABEL.daily;
  return POSTING_LABEL.every(Math.round(avgGapDays));
}

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export function costLabel(
  quote: DiscoveryCostQuote | undefined,
  status: 'pending' | 'error' | 'success',
): string {
  if (status === 'pending') return COST_COPY.estimating;
  if (status === 'error' || !quote) return COST_COPY.unavailable;
  if (quote.lowUsd === quote.highUsd) return COST_COPY.exact(USD.format(quote.totalUsd));
  return COST_COPY.range(USD.format(quote.lowUsd), USD.format(quote.highUsd));
}
